import React, { useCallback, useMemo, useState } from "react"

import { IconButton, TextFieldProps } from "@mui/material"
import { useField, useFormikContext } from "formik"

import { VisibilityIcon, VisibilityOffIcon } from "@l2r-front/l2r-icons"

import { L2RTextField } from "../../components/TextField"
import type { TypographyVariant } from "../../types"

type FormikTextFieldProps = TextFieldProps & {
    labelVariant?: TypographyVariant,
    iconEnd?: React.ReactNode,
    alwaysDisplayError?: boolean,
}

export function FormikTextField(props: FormikTextFieldProps): React.ReactNode {

    const {
        fullWidth = true,
        variant = "outlined",
        color = "primary",
        labelVariant = "Desktop & Mobile/Heading/H3",
        margin = "normal",
        name = "",
        alwaysDisplayError = false,
        ...otherProps
    } = props
    const [{ value: _fieldPropValue, ...fieldProps }, metaProps] = useField<string>({ name: name })
    const { setStatus } = useFormikContext()

    const handleChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            fieldProps.onChange(event)
            setStatus()
            props.onChange?.(event)
        },
        [fieldProps, setStatus, props]
    )

    const handleBlur = useCallback(
        (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            fieldProps.onBlur(event)
            props.onBlur?.(event)
        },
        [fieldProps, props]
    )

    const isPassword = useMemo(() => {
        return props.type === "password"
    }, [props.type])

    const [showPassword, setShowPassword] = useState(!isPassword)

    return (
        <L2RTextField
            fullWidth={fullWidth}
            variant={variant}
            color={color}
            labelVariant={labelVariant}
            margin={margin}
            {...otherProps}
            {...fieldProps}
            value={props?.value ?? metaProps.value}
            {...((metaProps.error && (metaProps.touched || alwaysDisplayError)) ? {
                error: true,
                helperText: metaProps.error,
            } : {})}
            onBlur={handleBlur}
            onChange={handleChange}
            iconEnd={isPassword ? (
                <IconButton onClick={() => setShowPassword(previousState => !previousState)} id={`show-${props.id}`}>
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>) : props.iconEnd}
            type={(isPassword && showPassword) ? "text" : props.type}
        />
    )
}